/**
 * Signing in: the full page, and the same form as a sheet over whatever was open when a session
 * ended (nothing under it is lost). The set-up link page, where someone chooses their password.
 */

import { PASSWORD_MIN } from '@mawkeb/shared/api';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { navigate } from '../../router';
import { session, useSession } from '../../state/session';
import { HallDrawing } from '../HallDrawing';
import { Eye } from '../icons';

function Password({ label, value, onChange, autoComplete, invalid, describedBy }: { label: string; value: string; onChange: (v: string) => void; autoComplete: string; invalid?: boolean; describedBy?: string }) {
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    <div className="input">
      <label htmlFor={id}>{label}</label>
      <div className="with-button">
        <input id={id} type={shown ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid || undefined} aria-describedby={describedBy} required />
        <button type="button" className="icon" aria-label={shown ? 'Hide the password' : 'Show the password'} aria-pressed={shown} onClick={() => setShown(!shown)}>
          <Eye off={shown} />
        </button>
      </div>
    </div>
  );
}

function SignInForm({ onDone, compact = false }: { onDone?: () => void; compact?: boolean }) {
  const remembered = useSession((s) => s.me?.email ?? '');
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const problemId = useId();
  const emailId = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const { me } = await api.signIn(email, password);
      session().signedIn(me);
      onDone?.();
    } catch (err) {
      setProblem(err instanceof ApiError ? (err.status === 'offline' ? 'There’s no connection. Check it, then try again.' : err.message) : 'Signing in didn’t work. Try again.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="signin-form" onSubmit={submit} noValidate>
      <div className="input">
        <label htmlFor={emailId}>Email</label>
        <input id={emailId} type="email" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={problem ? true : undefined} aria-describedby={problem ? problemId : undefined} required autoFocus={!remembered} />
      </div>
      <Password label="Password" value={password} onChange={setPassword} autoComplete="current-password" invalid={!!problem} describedBy={problem ? problemId : undefined} />
      {problem && (
        <p className="form-problem" id={problemId} role="alert">
          {problem}
        </p>
      )}
      <button type="submit" className="primary big" disabled={busy || !email || !password}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      {!compact && <p className="form-note">New here, or forgot your password? Ask an admin for a set-up link.</p>}
    </form>
  );
}

function Brand() {
  return (
    <div className="brand">
      <img src="/icons/icon-192.png" alt="" width="44" height="44" />
      <div>
        <strong>Venue</strong>
        <span>Mawkeb Saheb al-Zaman</span>
      </div>
    </div>
  );
}

export function SignInPage() {
  return (
    <main className="signin">
      <HallDrawing model={null} className="signin-drawing" />
      <section className="signin-card">
        <Brand />
        <h1>Sign in to plan the hall</h1>
        <SignInForm />
      </section>
      <p className="signin-caption">Zahraa Hall, Taha Foundation</p>
    </main>
  );
}

/** Over the page when a session ended while the app was open: sign in and carry on where you were. */
export function SignInSheet() {
  const ended = useSession((s) => s.ended);
  if (!ended) return null;
  return (
    <div className="dialog-backdrop">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="signin-again">
        <header>
          <h2 id="signin-again">Sign in again</h2>
        </header>
        <div className="dialog-body">
          <p className="lead-text">Your session ended. Sign in to carry on; nothing you were doing is lost.</p>
          <SignInForm compact />
          <button type="button" className="text-button" onClick={() => (session().signedOut(), navigate('/', { force: true }))}>
            Sign in as someone else
          </button>
        </div>
      </div>
    </div>
  );
}

export function WelcomePage() {
  const [token] = useState(() => location.hash.slice(1));
  const [who, setWho] = useState<{ email: string; name: string } | null>(null);
  const [gone, setGone] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hintId = useId();

  useEffect(() => {
    // the token leaves the address bar (and the browser history) as soon as it's read
    history.replaceState(null, '', '/welcome');
    api
      .setupCheck(token)
      .then(setWho)
      .catch((e: ApiError) => setGone(e.status === 'offline' ? 'There’s no connection. Connect, then open the link again.' : e.message));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (password !== again) return setProblem('The two passwords aren’t the same.');
    setBusy(true);
    setProblem(null);
    try {
      const { me } = await api.setup(token, password);
      session().signedIn(me);
      navigate('/', { replace: true, force: true });
    } catch (err) {
      const e2 = err as ApiError;
      if (e2.status === 404) setGone(e2.message);
      else setProblem(e2.status === 'offline' ? 'There’s no connection. Check it, then try again.' : e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <HallDrawing model={null} className="signin-drawing" />
      <section className="signin-card">
        <Brand />
        {gone ? (
          <>
            <h1>This link doesn’t work any more</h1>
            <p className="lead-text">{gone}</p>
            <button type="button" className="secondary big" onClick={() => navigate('/', { replace: true, force: true })}>
              Go to sign in
            </button>
          </>
        ) : !who ? (
          <p className="lead-text">Checking your link…</p>
        ) : (
          <>
            <h1>Choose your password, {who.name.split(' ')[0]}</h1>
            <form className="signin-form" onSubmit={submit} noValidate>
              <input type="email" autoComplete="username" value={who.email} readOnly hidden />
              <p className="form-note">You’ll sign in as {who.email}.</p>
              <Password label="New password" value={password} onChange={setPassword} autoComplete="new-password" describedBy={hintId} invalid={!!problem} />
              <p className="form-note" id={hintId}>
                At least {PASSWORD_MIN} characters. A short phrase only you would think of works well, and your phone can remember it.
              </p>
              <Password label="The same again" value={again} onChange={setAgain} autoComplete="new-password" invalid={!!problem} />
              {problem && (
                <p className="form-problem" role="alert">
                  {problem}
                </p>
              )}
              <button type="submit" className="primary big" disabled={busy || !password || !again}>
                {busy ? 'Saving…' : 'Save password and sign in'}
              </button>
            </form>
          </>
        )}
      </section>
    </main>
  );
}
